import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { Link, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MrtColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { ProductCompany, ProductOrganizationActivityData } from "src/api/client-api/products/useGetOneProduct";
import { InputBox } from "src/components/ActivityDataGrid/Box/InputBox";
import { SalesBox } from "src/components/ActivityDataGrid/Box/SalesBox";
import { Company } from "src/api/client-api/useUserNew";
import { Campaign } from "src/api/client-api/useCampaign";
import { OrganizationActivitiesFormData, isOwnShare } from "./activitiesSpace";

type ProductOrganizationActivityDataKeys = keyof Pick<ProductOrganizationActivityData, "quantity">;

export const useActivitiesColumns = (
    activitiesData: OrganizationActivitiesFormData,
    setActivitiesData: (value: OrganizationActivitiesFormData) => void,
    toggleEditMode: boolean,
): MrtColumns<ProductCompany> => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentCampaign, selectedCompany } = useFoodpilotContext();

    const columnHelper = createMRTColumnHelper<ProductCompany>();

    const mrt_columns = [
        columnHelper.accessor("organization.name", {
            header: t("product.activity_data.organization", "Organization"),
            Cell: (value) => (
                <Link sx={{ color: `${theme.palette.primary[700]}`, textDecoration: "none" }} variant="h5">
                    {value.renderedCellValue}
                </Link>
            ),
        }),
        columnHelper.accessor("productOrganizationActivityDatas.quantity", {
            header: t("product.activity_data.organization.quantity", "Quantity"),
            Cell: (value) => {
                const productCompanyId = value.row.original.id;

                if (!isOwnShare(value.row.original, selectedCompany?.id)) {
                    return "-";
                }

                const activityData = activitiesData[productCompanyId] ?? null;

                const quantity = activityData?.quantity ?? 0;

                return toggleEditMode ?
                        <InputBox
                            value={quantity.toString()}
                            unit={"T."}
                            updateActivities={(newValue) =>
                                updateActivitiesData(
                                    productCompanyId,
                                    newValue,
                                    "quantity",
                                    activitiesData,
                                    setActivitiesData,
                                    currentCampaign,
                                    selectedCompany,
                                )
                            }
                        />
                    :   <SalesBox value={quantity} unit={"T."} />;
            },
        }),
    ] as MRT_ColumnDef<ProductCompany, string>[];

    return mrt_columns;
};

const updateActivitiesData = (
    productCompanyId: number | undefined,
    value: string,
    key: ProductOrganizationActivityDataKeys,
    activitiesData: OrganizationActivitiesFormData,
    setActivitiesData: (value: OrganizationActivitiesFormData) => void,
    campaign: Campaign | null,
    company: Company | null,
) => {
    if (!productCompanyId || !campaign || !company) return;

    activitiesData[productCompanyId] = {
        ...activitiesData[productCompanyId],
        [key]: parseInt(value || "0"),
    };

    setActivitiesData({ ...activitiesData });
};
